import api from "@services/rest";

/** Where the users are read from: the CRM (TopSys) database, or the customer's TopconFLEET API. */
export type LegacySource = "CRM" | "FLEET";

/** Whether this environment can run legacy migrations. */
export interface LegacyMigrationStatus {
  /** The CRM source is configured on this environment. */
  configured: boolean;
  /** Why the feature is unavailable — shown to the operator when `configured` is false. */
  configurationProblem: string | null;
  /** The FLEET source needs no environment setting: each customer's FLEET URL and token come from the Tenant Manager. */
  fleetAvailable: boolean;
}

export interface LegacyMigrationTenant {
  id: string;
  name: string;
  alias: string;
  active: boolean;
  /** FLEET source only: FLEET is contracted in the customer's Modules / Products. */
  fleetContracted: boolean | null;
}

/** A module of the target tenant and whether the tenant has contracted it. */
export interface ContractedModule {
  module: string;
  name: string;
  contracted: boolean;
}

/** One legacy user, as read from the CRM or FLEET. Never carries the password. */
export interface LegacyMigrationCandidate {
  legacyId: string;
  email: string;
  name: string;
  hasPassword: boolean;
  /**
   * The legacy password passes the B2C password rule and becomes the Identity
   * password, typed in uppercase as the Topcon Legado stores it. False: a new
   * user gets the password e-mail instead.
   */
  passwordKept: boolean;
  eligible: boolean;
  skipReason: string | null;
  modules: string[];
  /** Legacy areas without an SSO product (FIN, COM…): the lock defaults to off when there are any. */
  otherLegacyAreas: string[];
  /** The Topcon Legado password was already locked by Identity. */
  legacyLocked: boolean;
  /** A user with this e-mail already exists in the tenant: migrating again only adds missing modules. */
  inIdentity: boolean;
  /** FLEET only: an admin in FLEET. Never becomes an Identity admin. */
  legacyAdmin: boolean;
  /** FLEET only: already signs in to FLEET through Identity, so their FLEET password can be locked now. */
  signedInWithIdentity: boolean;
}

export interface LegacyMigrationExtraction {
  tenantId: string;
  tenantName: string;
  source: LegacySource;
  sourceRows: number;
  candidates: LegacyMigrationCandidate[];
  contractedModules: ContractedModule[];
}

/** One user of a batch, as the screen sends it back for provisioning. */
export interface LegacyMigrationBatchItem {
  legacyId: string;
  email: string;
  modules: string[];
  /** Lock this user's Topcon Legado password once provisioning succeeds. */
  lockLegacyPassword: boolean;
}

export interface LegacyMigrationOutcome {
  legacyId: string;
  email: string;
  outcome: string;
  reason: string | null;
  passwordPreserved: boolean;
  resetEmailSent: boolean;
  modulesGranted: string[];
  /** The Topcon Legado login is locked after this batch, newly or already. */
  legacyLocked: boolean;
  /** Code explaining the lock; null when no lock was requested or attempted. */
  legacyLockReason: string | null;
}

export interface LegacyMigrationBatchResult {
  outcomes: LegacyMigrationOutcome[];
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  passwordsPreservedCount: number;
  resetEmailsSentCount: number;
  /** Topcon Legado passwords this batch locked. */
  legacyLockedCount: number;
}

/** Someone who holds the right to use the legacy migration. */
export interface LegacyMigrationManager {
  userId: string;
  userName: string;
  userEmail: string;
  grantedAt: string;
  grantedByUserId: string | null;
  grantedByName: string | null;
}

/** An admin who could be given that right. */
export interface LegacyMigrationManagerCandidate {
  userId: string;
  userName: string;
  userEmail: string;
}

/** Error body returned by every failed call of this API. */
export interface LegacyMigrationError {
  error: string;
  errorCode: string | null;
  /** HTTP status, so the caller can tell 401 (session) from 403 (not an operator). */
  status: number;
}

const BASE = "/api/legacy-migration";

/**
 * Typed client for the legacy migration API. REST, like the provisioning
 * endpoints, because it acts across tenants. The axios instance has no
 * interceptors, so each call attaches the bearer token itself.
 */
const legacyMigrationService = (getToken: () => Promise<string>) => {

  const authorize = async () => ({ headers: { Authorization: `Bearer ${await getToken()}` } });

  const get = async <T, >(path: string): Promise<T> => (await api.get<T>(`${BASE}${path}`, await authorize())).data;

  const post = async <T, >(path: string, body: unknown = {}): Promise<T> => (
    await api.post<T>(`${BASE}${path}`, body, await authorize())
  ).data;

  return {
    getStatus: (): Promise<LegacyMigrationStatus> => get<LegacyMigrationStatus>("/status"),

    listTenants: (source: LegacySource): Promise<LegacyMigrationTenant[]> => (
      get<LegacyMigrationTenant[]>(`/tenants?source=${source}`)
    ),

    extract: (tenantId: string, source: LegacySource): Promise<LegacyMigrationExtraction> => (
      post<LegacyMigrationExtraction>(`/tenants/${tenantId}/extract?source=${source}`)
    ),

    provision: (
      tenantId: string,
      source: LegacySource,
      users: LegacyMigrationBatchItem[]
    ): Promise<LegacyMigrationBatchResult> => (
      post<LegacyMigrationBatchResult>(`/tenants/${tenantId}/provision?source=${source}`, { users })
    ),

    listManagers: (): Promise<LegacyMigrationManager[]> => get<LegacyMigrationManager[]>("/access/managers"),

    listManagerCandidates: (): Promise<LegacyMigrationManagerCandidate[]> => (
      get<LegacyMigrationManagerCandidate[]>("/access/candidates")
    ),

    grantAccess: async (userId: string): Promise<void> => {
      await post<unknown>(`/access/managers/${userId}`);
    },

    revokeAccess: async (userId: string): Promise<void> => {
      await api.delete(`${BASE}/access/managers/${userId}`, await authorize());
    }
  };
};

/** Normalises an axios rejection into the API's error envelope. */
export const toLegacyMigrationError = (error: unknown): LegacyMigrationError => {
  const { response } = error as { response?: { status?: number; data?: Partial<LegacyMigrationError> } };

  return {
    error: response?.data?.error ?? (error as Error)?.message ?? "Unexpected error.",
    errorCode: response?.data?.errorCode ?? null,
    status: response?.status ?? 0
  };
};

export default legacyMigrationService;
